/**
 * Outil « Angles » (Boîte à outils, 6e → 4e).
 *
 * Rapporteur : deux graduations (0 à droite à l'extérieur, 0 à gauche à
 * l'intérieur) ; on tourne le côté [OB) au doigt ou aux flèches ; nature de
 * l'angle, bissectrice ; défis « lire » (le piège de la double graduation
 * est nommé) et « construire ». Triangle : sommets sur le quadrillage,
 * angles arrondis au degré dont la somme reste 180, triangles particuliers ;
 * dès la 5e, « pourquoi 180° » par la parallèle et les alternes-internes.
 * Parallèles (dès la 5e) : deux droites, une sécante ; opposés par le
 * sommet, correspondants, alternes-internes, égaux exactement quand les
 * droites sont parallèles. Calculs : `angles.ts`.
 */

import { useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import { schoolLevelRank } from '@jeraisonne/db/cards';
import { MathText, cn } from '@jeraisonne/ui';
import type { ToolProps } from '../../tool-types';
import {
  Note,
  ParamSlider,
  PRIMARY_BUTTON,
  SECONDARY_BUTTON,
  Segmented,
  ToggleChip,
} from '../../ui/controls';
import { frenchSpaces } from '../../ui/format';
import { makeFrame, snapPoint, type Viewport } from '../../ui/frame';
import {
  angleArcPath,
  DragHandle,
  FIGURE_BG_CLASS,
  HaloText,
  INK,
  MUTED,
  PlotGrid,
} from '../../ui/svg-parts';
import { useDrag } from '../../ui/use-drag';
import {
  angleKind,
  cornerAngle,
  kindSay,
  pairSay,
  PAIRS,
  protractor,
  readingFeedback,
  triangleInfo,
  type Corner,
  type Pair,
  type Pt,
} from './angles';

type Mode = 'protractor' | 'triangle' | 'parallel';

const C1 = 'var(--color-primary-500)';
const C2 = 'var(--color-accent-500)';
const C3 = 'var(--color-success-600)';
const INNER = 'var(--color-error-500)';
const VB_W = 340;
const deg = (v: number) => `${v}°`;

export function AnglesTool({
  level,
  rng = Math.random,
}: ToolProps & { level?: string | null; rng?: () => number }) {
  const rank = Math.min(4, Math.max(1, schoolLevelRank(level) ?? 4));
  const [mode, setMode] = useState<Mode>('protractor');
  const options: Array<{ value: Mode; label: string }> = [
    { value: 'protractor', label: 'Rapporteur' },
    { value: 'triangle', label: 'Triangle' },
    { value: 'parallel', label: rank >= 2 ? 'Parallèles' : 'Droites sécantes' },
  ];
  return (
    <div className="space-y-4">
      <Segmented ariaLabel="Mode" value={mode} options={options} onChange={setMode} />
      <div hidden={mode !== 'protractor'} className="space-y-4">
        <ProtractorMode rng={rng} />
      </div>
      <div hidden={mode !== 'triangle'} className="space-y-4">
        <TriangleMode rank={rank} />
      </div>
      <div hidden={mode !== 'parallel'} className="space-y-4">
        <ParallelMode rank={rank} />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Rapporteur                                                          */
/* ------------------------------------------------------------------ */

const PO = { x: 170, y: 186 };
const PR = 140;
const PH = 206;
/** point à l'angle a (degrés, depuis la droite, sens inverse des aiguilles) */
const polar = (a: number, r: number) => ({
  x: PO.x + r * Math.cos((a * Math.PI) / 180),
  y: PO.y - r * Math.sin((a * Math.PI) / 180),
});

type Quiz =
  | { kind: 'read'; answer: string; say: string | null }
  | { kind: 'build'; target: number; checked: number | null };

/** secteur de l'angle : de [OA) à [OB), dans le bon sens même pour 180° */
function sectorPath(firstLeft: boolean, phi: number, r: number): string {
  const a = polar(firstLeft ? 180 : 0, r);
  const b = polar(phi, r);
  const f = (n: number) => n.toFixed(2);
  return `M ${PO.x} ${PO.y} L ${f(a.x)} ${f(a.y)} A ${r} ${r} 0 0 ${firstLeft ? 1 : 0} ${f(b.x)} ${f(b.y)} Z`;
}

function ProtractorMode({ rng }: { rng: () => number }) {
  const [firstLeft, setFirstLeft] = useState(false);
  const [phi, setPhi] = useState(50);
  const [bisector, setBisector] = useState(false);
  const [quiz, setQuiz] = useState<Quiz | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const dragging = useRef(false);
  const r = protractor(firstLeft, phi);
  // défi « lire » : B ne bouge plus ; défi « construire » : mesure cachée tant qu'on n'a pas vérifié
  const locked = quiz?.kind === 'read';
  const hidden =
    (quiz?.kind === 'read' && quiz.say === null) ||
    (quiz?.kind === 'build' && quiz.checked !== phi);
  const setMeasure = (m: number) => {
    const v = Math.min(180, Math.max(0, m));
    setPhi(firstLeft ? 180 - v : v);
  };

  const setFromPointer = (e: PointerEvent<SVGSVGElement>) => {
    const svg = svgRef.current;
    if (!svg || locked) return;
    const b = svg.getBoundingClientRect();
    if (!b.width || !b.height) return;
    const x = ((e.clientX - b.left) / b.width) * VB_W;
    const y = ((e.clientY - b.top) / b.height) * PH;
    const a = (Math.atan2(PO.y - y, x - PO.x) * 180) / Math.PI;
    setPhi(Math.round(Math.min(180, Math.max(0, a < -90 ? 180 : a))));
  };
  // convention des curseurs : → et ↑ augmentent la MESURE ; Début / Fin
  const onKey = (e: KeyboardEvent<SVGGElement>) => {
    if (locked) return;
    const step = e.shiftKey ? 10 : 1;
    const m = r.measure;
    const next =
      e.key === 'ArrowRight' || e.key === 'ArrowUp'
        ? m + step
        : e.key === 'ArrowLeft' || e.key === 'ArrowDown'
          ? m - step
          : e.key === 'Home'
            ? 0
            : e.key === 'End'
              ? 180
              : null;
    if (next === null) return;
    e.preventDefault();
    setMeasure(next);
  };

  const newRead = () => {
    setFirstLeft(rng() < 0.5);
    setPhi(10 + Math.floor(rng() * 161));
    setQuiz({ kind: 'read', answer: '', say: null });
  };
  const newBuild = () => {
    let target = r.measure;
    while (target === r.measure) target = 5 * (2 + Math.floor(rng() * 33));
    setQuiz({ kind: 'build', target, checked: null });
  };
  const built = quiz?.kind === 'build' && quiz.checked === phi && r.measure === quiz.target;

  const end = polar(phi, PR + 8);
  const first = polar(firstLeft ? 180 : 0, PR + 8);
  const arcFrom = firstLeft ? 180 : 0;
  const ticks = Array.from({ length: 181 }, (_, i) => i);
  // les 0 et 180 des graduations : un peu au-dessus de la ligne de base (sinon sous les côtés)
  const lift = (t: number) => (t === 0 || t === 180 ? -10 : 0);
  return (
    <>
      <div className="flex flex-wrap gap-2">
        <ToggleChip
          on={firstLeft}
          onClick={() => {
            setFirstLeft((f) => !f);
            setQuiz(null);
          }}
        >
          Premier côté à gauche
        </ToggleChip>
        <ToggleChip on={bisector} onClick={() => setBisector((b) => !b)}>
          Bissectrice
        </ToggleChip>
      </div>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${VB_W} ${PH}`}
        className={cn('w-full touch-none select-none rounded-2xl', FIGURE_BG_CLASS)}
        role="group"
        aria-label="Rapporteur posé sur l’angle AOB"
        onPointerDown={(e) => {
          dragging.current = true;
          e.currentTarget.setPointerCapture?.(e.pointerId);
          setFromPointer(e);
        }}
        onPointerMove={(e) => dragging.current && setFromPointer(e)}
        onPointerUp={() => (dragging.current = false)}
        onPointerCancel={() => (dragging.current = false)}
        data-testid="protractor"
      >
        {/* le rapporteur */}
        <path
          d={`M ${PO.x - PR} ${PO.y} A ${PR} ${PR} 0 0 1 ${PO.x + PR} ${PO.y} Z`}
          fill="var(--color-primary-500)"
          fillOpacity={0.06}
          stroke={MUTED}
          strokeWidth={1.5}
        />
        {ticks.map((t) => {
          const long = t % 10 === 0;
          const mid = t % 5 === 0;
          const a = polar(t, PR);
          const b = polar(t, PR - (long ? 12 : mid ? 8 : 4));
          return (
            <line
              key={t}
              x1={a.x}
              y1={a.y}
              x2={b.x}
              y2={b.y}
              stroke={MUTED}
              strokeWidth={long ? 1.3 : 0.7}
            />
          );
        })}
        {ticks
          .filter((t) => t % 10 === 0)
          .map((t) => {
            const o = polar(t, PR - 22);
            const i = polar(t, PR - 42);
            return (
              <g key={`l${t}`} aria-hidden>
                <text x={o.x} y={o.y + 4 + lift(t)} textAnchor="middle" fontSize={12} fill={INK}>
                  {t}
                </text>
                {t % 20 === 0 && (
                  <text
                    x={i.x}
                    y={i.y + 4 + lift(t)}
                    textAnchor="middle"
                    fontSize={12}
                    fill={INNER}
                  >
                    {180 - t}
                  </text>
                )}
              </g>
            );
          })}
        {/* l'angle */}
        {!hidden && r.measure > 0 && (
          <path d={sectorPath(firstLeft, phi, 34)} fill={C2} fillOpacity={0.3} />
        )}
        <line x1={PO.x} y1={PO.y} x2={first.x} y2={first.y} stroke={C1} strokeWidth={3} />
        <HaloText
          x={first.x + (firstLeft ? 4 : -4)}
          y={first.y - 8}
          textAnchor={firstLeft ? 'start' : 'end'}
          fontSize={14}
          fontWeight={700}
          fill={INK}
        >
          A
        </HaloText>
        {bisector && !hidden && (
          <line
            x1={PO.x}
            y1={PO.y}
            x2={polar((phi + arcFrom) / 2, PR + 4).x}
            y2={polar((phi + arcFrom) / 2, PR + 4).y}
            stroke={C3}
            strokeWidth={2}
            strokeDasharray="6 4"
          />
        )}
        <line x1={PO.x} y1={PO.y} x2={end.x} y2={end.y} stroke={C1} strokeWidth={3} />
        <g
          role="slider"
          tabIndex={0}
          aria-label="Côté [OB) : flèches pour le tourner"
          aria-valuemin={0}
          aria-valuemax={180}
          aria-valuenow={hidden ? undefined : r.measure}
          aria-valuetext={hidden ? 'mesure cachée' : `angle AOB : ${r.measure} degrés`}
          aria-disabled={locked || undefined}
          onKeyDown={onKey}
          className="cursor-grab outline-none [&:focus-visible_.drag-ring]:opacity-100"
        >
          <circle cx={end.x} cy={end.y} r={16} fill={C1} opacity={0.15} />
          <circle
            className="drag-ring"
            cx={end.x}
            cy={end.y}
            r={20}
            fill="none"
            stroke="var(--color-primary-400)"
            strokeWidth={2.5}
            opacity={0}
          />
          <circle cx={end.x} cy={end.y} r={6} fill={C1} />
        </g>
        <HaloText x={end.x + 12} y={end.y - 8} fontSize={14} fontWeight={700} fill={INK}>
          B
        </HaloText>
        <circle cx={PO.x} cy={PO.y} r={3.5} fill={INK} />
        <HaloText
          x={PO.x}
          y={PO.y + 16}
          textAnchor="middle"
          fontSize={13}
          fontWeight={700}
          fill={INK}
        >
          O
        </HaloText>
      </svg>

      <div aria-live="polite" className="space-y-2">
        {!hidden && (
          <Note live={false}>
            <span data-testid="pr-measure" className="block">
              <MathText>{`$\\widehat{AOB} = ${r.measure}^\\circ$`}</MathText>
              {frenchSpaces(
                ` : ${kindSay(r.measure)}${bisector && r.measure > 0 ? ` La bissectrice le partage en deux angles de ${String(r.measure / 2).replace('.', ',')}°.` : ''}`,
              )}
            </span>
            <span className="mt-1 block text-xs">
              {frenchSpaces(
                `On lit la graduation ${r.good === 'outer' ? 'extérieure' : 'intérieure'} : celle qui a son 0 sur le côté [OA).`,
              )}
            </span>
          </Note>
        )}
        {quiz?.kind === 'read' && (
          <form
            className="flex flex-wrap items-end gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              const t = quiz.answer.replace('°', '').replace(',', '.').trim();
              const v = Number(t);
              if (!Number.isFinite(v) || t === '') return;
              setQuiz({ ...quiz, say: readingFeedback(firstLeft, phi, Math.round(v)) });
            }}
          >
            <label className="block">
              <span className="text-ink-700 text-sm font-semibold">Mesure lue (en °)</span>
              <input
                type="text"
                inputMode="numeric"
                value={quiz.answer}
                onChange={(e) => setQuiz({ ...quiz, answer: e.target.value })}
                className="border-ink-200 text-ink-900 mt-1 block min-h-11 w-28 rounded-xl border bg-transparent px-3 text-lg font-bold"
              />
            </label>
            <button type="submit" className={PRIMARY_BUTTON}>
              Vérifier
            </button>
          </form>
        )}
        {quiz?.kind === 'read' && quiz.say && (
          <Note live={false} tone={quiz.say.startsWith('Oui') ? 'success' : 'error'}>
            <span data-testid="pr-feedback">{frenchSpaces(quiz.say)}</span>
          </Note>
        )}
        {quiz?.kind === 'build' && (
          <>
            <Note live={false} tone={built ? 'success' : 'neutral'}>
              <span data-testid="pr-build">
                {frenchSpaces(
                  built
                    ? `Bravo : l’angle AOB mesure bien ${quiz.target}°.`
                    : quiz.checked === phi
                      ? `Pas encore : il faut ${quiz.target}°. Tourne le côté [OB) puis vérifie à nouveau.`
                      : `Tourne le côté [OB) pour construire un angle de ${quiz.target}°, puis vérifie.`,
                )}
              </span>
            </Note>
            {!built && (
              <button
                type="button"
                className={PRIMARY_BUTTON}
                onClick={() => setQuiz({ ...quiz, checked: phi })}
              >
                Vérifier mon angle
              </button>
            )}
          </>
        )}
        <div className="flex flex-wrap gap-2">
          <button type="button" className={SECONDARY_BUTTON} onClick={newRead}>
            Défi : lire un angle
          </button>
          <button type="button" className={SECONDARY_BUTTON} onClick={newBuild}>
            Défi : construire
          </button>
          {quiz && (
            <button type="button" className={SECONDARY_BUTTON} onClick={() => setQuiz(null)}>
              Fin du défi
            </button>
          )}
        </div>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Triangle                                                            */
/* ------------------------------------------------------------------ */

const TSIZE = 340;
const TVP: Viewport = { xMin: 0, xMax: 10, yMin: 0, yMax: 10 };
const tframe = makeFrame(TVP, TSIZE);
type V = 'A' | 'B' | 'C';
const VS: readonly V[] = ['A', 'B', 'C'];
const VCOL = [C1, C2, C3];

function TriangleMode({ rank }: { rank: number }) {
  const [t, setT] = useState<Record<V, Pt>>({
    A: { x: 1, y: 2 },
    B: { x: 9, y: 2 },
    C: { x: 4, y: 8 },
  });
  const [why, setWhy] = useState(false);
  const svgRef = useRef<SVGSVGElement>(null);
  const info = triangleInfo(t.A, t.B, t.C);
  const drag = useDrag<V>({
    svgRef,
    frame: tframe,
    ids: VS,
    keyStep: 1,
    pointOf: (v) => t[v],
    onDrag: (v, p) => {
      const s = snapPoint(p, TVP, 1, 0.5);
      if (VS.some((o) => o !== v && t[o].x === s.x && t[o].y === s.y)) return;
      setT({ ...t, [v]: s });
    },
  });
  const P = (v: V) => ({ x: tframe.tx(t[v].x), y: tframe.ty(t[v].y) });
  const [pa, pb, pc] = [P('A'), P('B'), P('C')];
  const arcs: Array<[typeof pa, typeof pa, typeof pa]> = [
    [pa, pb, pc],
    [pb, pc, pa],
    [pc, pa, pb],
  ];
  // la parallèle à (AB) par C
  const ux = pb.x - pa.x;
  const uy = pb.y - pa.y;
  const ul = Math.hypot(ux, uy) || 1;
  const far = 400;
  const left = { x: pc.x - (ux / ul) * far, y: pc.y - (uy / ul) * far };
  const right = { x: pc.x + (ux / ul) * far, y: pc.y + (uy / ul) * far };
  const sector = (v: typeof pa, a: typeof pa, b: typeof pa, r: number) =>
    `${angleArcPath(v.x, v.y, a.x, a.y, b.x, b.y, r)} L ${v.x} ${v.y} Z`;
  return (
    <>
      <div className="mx-auto w-full max-w-[24rem]">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${TSIZE} ${TSIZE}`}
          className={cn(
            'border-ink-100 block aspect-square w-full touch-none select-none rounded-2xl border',
            FIGURE_BG_CLASS,
          )}
          role="group"
          aria-label="Triangle ABC sur quadrillage"
          {...drag.svgProps}
        >
          <PlotGrid frame={tframe} step={1} labels={false} axes={false} />
          <polygon
            points={`${pa.x},${pa.y} ${pb.x},${pb.y} ${pc.x},${pc.y}`}
            fill={C1}
            fillOpacity={0.06}
            stroke={INK}
            strokeWidth={2}
          />
          {!info.degenerate &&
            arcs.map(([v, a, b], i) => (
              <path
                key={i}
                d={sector(v, a, b, 32)}
                fill={VCOL[i]}
                fillOpacity={0.35}
                stroke={VCOL[i]}
                strokeWidth={1.5}
              />
            ))}
          {why && !info.degenerate && (
            <g aria-hidden>
              <line
                x1={left.x}
                y1={left.y}
                x2={right.x}
                y2={right.y}
                stroke={MUTED}
                strokeWidth={1.5}
                strokeDasharray="6 4"
              />
              {/* alternes-internes : Â en C du côté de A, B̂ en C du côté de B */}
              <path
                d={sector(pc, left, pa, 44)}
                fill={VCOL[0]}
                fillOpacity={0.35}
                stroke={VCOL[0]}
                strokeWidth={1.5}
              />
              <path
                d={sector(pc, pb, right, 44)}
                fill={VCOL[1]}
                fillOpacity={0.35}
                stroke={VCOL[1]}
                strokeWidth={1.5}
              />
            </g>
          )}
          {VS.map((v, i) => {
            const p = P(v);
            const g = { x: (pa.x + pb.x + pc.x) / 3, y: (pa.y + pb.y + pc.y) / 3 };
            const d = Math.hypot(p.x - g.x, p.y - g.y) || 1;
            return (
              <DragHandle
                key={v}
                x={p.x}
                y={p.y}
                color={VCOL[i]!}
                label={v}
                labelDx={((p.x - g.x) / d) * 22}
                labelDy={((p.y - g.y) / d) * 22}
                name={`Sommet ${v} (${t[v].x} ; ${t[v].y})`}
                active={drag.active === v}
                keyProps={drag.keyProps(v)}
              />
            );
          })}
        </svg>
      </div>
      <div aria-live="polite" className="space-y-2">
        <ul className="grid grid-cols-3 gap-2 text-center text-sm" data-testid="tri-angles">
          {VS.map((v, i) => (
            <li key={v} className="border-ink-100 rounded-xl border px-2 py-1.5">
              <span
                className="mr-1 inline-block h-3 w-3 rounded-full align-middle"
                style={{ background: VCOL[i] }}
                aria-hidden
              />
              <span className="font-semibold">{v}</span>
              <span className="text-ink-900 font-display block text-lg font-bold">
                {deg(info.shown[i]!)}
              </span>
              <span className="text-ink-500 block text-xs">
                {info.degenerate ? '' : angleKind(info.shown[i]!)}
              </span>
            </li>
          ))}
        </ul>
        <Note live={false} tone={info.degenerate ? 'error' : 'neutral'}>
          <span className="block" data-testid="tri-sum">
            {frenchSpaces(
              info.degenerate
                ? info.say
                : `${info.shown.map((v) => `${v}°`).join(' + ')} = 180° : la somme des angles d’un triangle fait toujours 180°.`,
            )}
          </span>
          {!info.degenerate && (
            <span className="mt-1 block" data-testid="tri-kind">
              {frenchSpaces(info.say)}
            </span>
          )}
          {!info.degenerate && info.exact.some((a) => Math.abs(a - Math.round(a)) > 1e-6) && (
            <span className="text-ink-500 mt-1 block text-xs">Mesures arrondies au degré.</span>
          )}
        </Note>
        {rank >= 2 && (
          <>
            <ToggleChip on={why} onClick={() => setWhy((w) => !w)}>
              Pourquoi 180° ?
            </ToggleChip>
            {why && (
              <Note live={false}>
                {frenchSpaces(
                  `On trace la parallèle à (AB) passant par C. La droite (AC) coupe ces deux parallèles : l’angle en A et l’angle bleu en C sont alternes-internes, donc égaux (${info.shown[0]}°). De même avec (BC) : l’angle en B et l’angle jaune en C (${info.shown[1]}°). Avec l’angle en C (${info.shown[2]}°), ils forment un angle plat : 180°.`,
                )}
              </Note>
            )}
          </>
        )}
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Parallèles                                                          */
/* ------------------------------------------------------------------ */

const LH = 250;
const L1Y = 82;
const L2C = { x: 170, y: 172 };

/** I₂ reste dans la figure : on rapproche la sécante de la verticale s'il le faut */
function fitAlpha(alpha: number, beta: number): number {
  const ok = (a: number) => {
    const s = { x: Math.cos((a * Math.PI) / 180), y: -Math.sin((a * Math.PI) / 180) };
    const l2 = { x: Math.cos((beta * Math.PI) / 180), y: -Math.sin((beta * Math.PI) / 180) };
    const S0 = { x: 170, y: (L1Y + L2C.y) / 2 };
    const det = s.x * -l2.y - s.y * -l2.x;
    const t = ((L2C.x - S0.x) * -l2.y - (L2C.y - S0.y) * -l2.x) / det;
    const x = S0.x + t * s.x;
    return x > 40 && x < VB_W - 40;
  };
  let a = alpha;
  while (!ok(a) && a !== 90) a += a < 90 ? 1 : -1;
  return a;
}

function ParallelMode({ rank }: { rank: number }) {
  const [alphaWanted, setAlpha] = useState(60);
  const [beta, setBeta] = useState(0);
  // 6e : opposés par le sommet seulement (le lexique) ; dès la 5e : parallèles
  const pairs: Pair[] = rank >= 2 ? ['opp', 'corr', 'alt'] : ['opp'];
  const [picked, setPair] = useState<Pair>(pairs.at(-1)!);
  const pair = pairs.includes(picked) ? picked : pairs[0]!;
  const alpha = fitAlpha(alphaWanted, beta);
  const s = { x: Math.cos((alpha * Math.PI) / 180), y: -Math.sin((alpha * Math.PI) / 180) };
  const l2 = { x: Math.cos((beta * Math.PI) / 180), y: -Math.sin((beta * Math.PI) / 180) };
  // la sécante passe par le milieu (170 ; 127)
  const S0 = { x: 170, y: (L1Y + L2C.y) / 2 };
  const I1 = { x: S0.x + ((L1Y - S0.y) / s.y) * s.x, y: L1Y };
  // intersection de la sécante et de d₂ : S0 + t s = L2C + u l2
  const det = s.x * -l2.y - s.y * -l2.x;
  const tt = ((L2C.x - S0.x) * -l2.y - (L2C.y - S0.y) * -l2.x) / det;
  const I2 = { x: S0.x + tt * s.x, y: S0.y + tt * s.y };
  const far = 500;
  const dirs = (line: 1 | 2): Record<Corner, [Pt, Pt]> => {
    const l = line === 1 ? { x: 1, y: 0 } : l2;
    const nl = { x: -l.x, y: -l.y };
    const ns = { x: -s.x, y: -s.y };
    return { ne: [l, s], nw: [s, nl], sw: [nl, ns], se: [ns, l] };
  };
  const sector = (I: Pt, line: 1 | 2, c: Corner, color: string) => {
    const [u, v] = dirs(line)[c];
    const a = { x: I.x + u.x * 10, y: I.y + u.y * 10 };
    const b = { x: I.x + v.x * 10, y: I.y + v.y * 10 };
    const mid = { x: u.x + v.x, y: u.y + v.y };
    const m = Math.hypot(mid.x, mid.y) || 1;
    const value = cornerAngle(line, c, alpha, beta);
    return (
      <g>
        <path
          d={`${angleArcPath(I.x, I.y, a.x, a.y, b.x, b.y, 28)} L ${I.x} ${I.y} Z`}
          fill={color}
          fillOpacity={0.4}
          stroke={color}
          strokeWidth={1.5}
        />
        <HaloText
          x={I.x + (mid.x / m) * 46}
          y={I.y + (mid.y / m) * 46 + 5}
          textAnchor="middle"
          fontSize={13}
          fontWeight={700}
          fill={INK}
        >
          {deg(value)}
        </HaloText>
      </g>
    );
  };
  const [[l1, c1], [l2n, c2]] = PAIRS[pair].corners;
  return (
    <>
      {pairs.length > 1 && (
        <Segmented
          ariaLabel="Angles à comparer"
          value={pair}
          options={pairs.map((v) => ({
            value: v,
            label: v === 'opp' ? 'Opposés' : PAIRS[v].label,
          }))}
          onChange={setPair}
        />
      )}
      <svg
        viewBox={`0 0 ${VB_W} ${LH}`}
        className={cn('w-full rounded-2xl', FIGURE_BG_CLASS)}
        role="img"
        aria-label={`Deux droites d1 et d2 coupées par une sécante. ${pairSay(pair, alpha, beta)}`}
      >
        <line x1={0} y1={L1Y} x2={VB_W} y2={L1Y} stroke={INK} strokeWidth={2} />
        <line
          x1={L2C.x - l2.x * far}
          y1={L2C.y - l2.y * far}
          x2={L2C.x + l2.x * far}
          y2={L2C.y + l2.y * far}
          stroke={INK}
          strokeWidth={2}
        />
        <line
          x1={S0.x - s.x * far}
          y1={S0.y - s.y * far}
          x2={S0.x + s.x * far}
          y2={S0.y + s.y * far}
          stroke={C1}
          strokeWidth={2}
        />
        <HaloText x={12} y={L1Y - 8} fontSize={14} fontWeight={700} fill={INK}>
          d₁
        </HaloText>
        <HaloText
          x={12}
          y={L2C.y + ((12 - L2C.x) / l2.x) * l2.y - 8}
          fontSize={14}
          fontWeight={700}
          fill={INK}
        >
          d₂
        </HaloText>
        {sector(l1 === 1 ? I1 : I2, l1, c1, C2)}
        {sector(l2n === 1 ? I1 : I2, l2n, c2, C3)}
      </svg>
      <ParamSlider
        name="Inclinaison de la sécante"
        ariaLabel="Inclinaison de la sécante"
        value={alpha}
        min={25}
        max={155}
        step={1}
        onChange={setAlpha}
        format={deg}
      />
      {rank >= 2 && (
        <>
          <ParamSlider
            name="Inclinaison de d₂"
            ariaLabel="Inclinaison de d2 par rapport à d1"
            value={beta}
            min={-20}
            max={20}
            step={1}
            onChange={setBeta}
            format={(v) => (v === 0 ? '0° (parallèles)' : `${v < 0 ? '−' : ''}${Math.abs(v)}°`)}
          />
          <button
            type="button"
            className={cn(SECONDARY_BUTTON, 'disabled:opacity-40')}
            onClick={() => setBeta(0)}
            disabled={beta === 0}
          >
            Rendre les droites parallèles
          </button>
        </>
      )}
      <Note>
        <span data-testid="par-say">{frenchSpaces(pairSay(pair, alpha, beta))}</span>
        {pair !== 'opp' && (
          <span className="mt-1 block text-xs">
            {frenchSpaces(
              'Réciproque : si deux angles alternes-internes (ou correspondants) sont égaux, alors les droites sont parallèles.',
            )}
          </span>
        )}
      </Note>
    </>
  );
}
